/**
 * Come è andato un giudizio, in forma compatta (SPEC-023 §6, F-126).
 *
 * ⚠️ **Quattro esiti e non tre.** `unmeasurable` non è un esito «neutro con
 * meno informazione»: è «non lo abbiamo osservato», e confonderlo con «il
 * titolo non si è mosso» riempirebbe la banda neutra di casi mai misurati. Ha
 * un tono suo e porta con sé il motivo.
 *
 * Il rendimento del titolo si mostra **accanto a quello dei suoi pari** — la
 * mediana dei titoli giudicati lo stesso giorno — perché un +3% in una
 * settimana in cui tutto è salito del 5% è un giudizio che ha perso.
 */

import { Badge } from '@/ui';
import { formatPercent } from '@/lib/format';
import type { InstrumentOutcome } from './api';
import './outcomes.css';

const STATUS_TONE: Record<InstrumentOutcome['status'], 'positive' | 'negative' | 'neutral'> = {
  hit: 'positive',
  miss: 'negative',
  neutral: 'neutral',
  unmeasurable: 'neutral',
};

const STATUS_LABEL: Record<InstrumentOutcome['status'], string> = {
  hit: 'azzeccato',
  miss: 'sbagliato',
  neutral: 'neutro',
  unmeasurable: 'non misurabile',
};

/** Un solo esito: `30 g · azzeccato +4,10%`. */
export function OutcomeBadge({ outcome }: { outcome: InstrumentOutcome }) {
  const title =
    outcome.status === 'unmeasurable'
      ? (outcome.unmeasurableReason ?? 'Non misurabile')
      : [
          outcome.returnPct !== null ? `rendimento ${formatPercent(outcome.returnPct)}` : null,
          outcome.peerReturnPct !== null ? `pari ${formatPercent(outcome.peerReturnPct)}` : null,
          outcome.targetErrorPct !== null ? `errore sull obiettivo ${formatPercent(outcome.targetErrorPct)}` : null,
        ]
          .filter(Boolean)
          .join(' · ');

  return (
    <Badge tone={STATUS_TONE[outcome.status]}>
      <span title={title}>
        {outcome.horizonDays} g · {STATUS_LABEL[outcome.status]}
        {outcome.status !== 'unmeasurable' && outcome.returnPct !== null && ` ${formatPercent(outcome.returnPct)}`}
      </span>
    </Badge>
  );
}

/**
 * Gli esiti di uno stesso giudizio ai vari orizzonti, in fila.
 *
 * ⚠️ Non si sceglie «il più significativo»: a 7 giorni si misura se il digest
 * del mattino aveva ragione quella settimana, a 90 se la tesi ha tenuto. Sono
 * domande diverse, e mostrarne una sola risponderebbe a caso.
 */
export function OutcomeBadges({ outcomes }: { outcomes: readonly InstrumentOutcome[] }) {
  if (outcomes.length === 0) {
    return <span className="cell--minor">non ancora maturo</span>;
  }
  return (
    <span className="outcome-badges">
      {[...outcomes]
        .sort((a, b) => a.horizonDays - b.horizonDays)
        .map((outcome) => (
          <OutcomeBadge key={`${outcome.subjectId}:${outcome.horizonDays}`} outcome={outcome} />
        ))}
    </span>
  );
}
